import type {
  AccountingSheetReportType,
  DartConvertFormat,
} from "@/apis/dsd/route";
import {
  CONSOLIDATION_ITEMS,
  CURRENT_YEAR_KST,
  PERIOD_ITEMS,
  REPORT_FORMAT_ITEMS,
  YEAR_ITEMS,
  type AccountingSheetPeriod,
  type ConsolidationType,
} from "@/constants/accounting-sheet";
import {
  dartConvertErrorMessage,
  DSD_CONVERT_MUTATION_KEY,
  useConvertAccountingSheet,
  useConvertDart,
  useConvertDsdOrExcel,
} from "@/hooks/mutations/use-dsd-convert";
import { cn } from "@/lib/utils";
import { Button } from "@/toggle-ui-kit/button";
import { SelectInput } from "@/toggle-ui-kit/select-input";
import { ViewSwitchGroup } from "@/toggle-ui-kit/view-switch-group";
import { logHomeWork } from "@/utils/usage-log";
import { Link, Loader2, Repeat2 } from "lucide-react";
import { useId, useRef, useState } from "react";

import { FileDropZone } from "@/components/file-drop-zone";
import { Notice } from "./notice";

/**
 * 이 패널에서 시작한 변환만 가려내는 키. 같은 훅을 team-workspace 변환 화면도 쓰는데,
 * 거기서 시작한 변환이 끝나기 전에 홈으로 오면 열지도 않은 패널 때문에 STEP 이 잠긴다.
 */
export const HOME_CONVERT_MUTATION_KEY = [...DSD_CONVERT_MUTATION_KEY, "home"];

type Tab = "dsd-excel" | "dart" | "accounting-sheet";

const TABS: { value: Tab; label: string }[] = [
  { value: "dsd-excel", label: "DSD → 엑셀 변환" },
  { value: "dart", label: "공시 불러오기" },
  { value: "accounting-sheet", label: "정산표 → DSD 변환" },
];

// 집계에서 STEP 카드 로그와 묶이도록 steps.tsx 의 label 과 같게 쓴다
const STEP_LABEL = "기초 보고서 작업";

const DART_FORMAT_ITEMS: { value: DartConvertFormat; label: string }[] = [
  { value: "dsd", label: "DSD 파일로 변환" },
  { value: "xlsx", label: "엑셀 파일로 변환" },
];

const ConvertButton = ({
  pending,
  disabled,
  onClick,
  className,
}: {
  pending: boolean;
  disabled: boolean;
  onClick: () => void;
  className?: string;
}) => (
  <Button
    variant="solid"
    size={40}
    className={cn("shrink-0 bg-blue-500", className)}
    leftIcon={
      pending ? (
        <Loader2 className="animate-spin" strokeWidth={1.8} />
      ) : (
        <Repeat2 strokeWidth={1.8} />
      )
    }
    disabled={disabled || pending}
    onClick={onClick}
  >
    변환
  </Button>
);

const DsdExcelTab = () => {
  const [file, setFile] = useState<File | null>(null);
  const { mutate, isPending } = useConvertDsdOrExcel(HOME_CONVERT_MUTATION_KEY);

  return (
    <>
      <Notice>DSD 파일을 업로드해주세요</Notice>
      <FileDropZone
        accept=".dsd"
        hint="*.dsd 형식"
        file={file}
        onFileChange={setFile}
      />
      <ConvertButton
        pending={isPending}
        disabled={!file}
        className="mt-2"
        // 변환 중에도 교체할 수 있으니 보낸 파일 그대로일 때만 비운다
        onClick={() => {
          if (!file) return;
          logHomeWork(STEP_LABEL, "DSD → 엑셀 변환");
          mutate(file, {
            onSuccess: () => setFile((cur) => (cur === file ? null : cur)),
          });
        }}
      />
    </>
  );
};

const DART_URL_FORMAT_ERROR = "URL 형식이 아닙니다. 주소를 다시 확인해 주세요.";

const DartTab = () => {
  const [url, setUrl] = useState("");
  const [format, setFormat] = useState<DartConvertFormat>("dsd");
  /**
   * 에러는 한 번에 하나만 보여주므로 상태도 하나만 두고, "어느 값에 대한 판정인지"를
   * 함께 들고 있는다. 값이 바뀌면 지난 판정은 자동으로 무효 — 변환을 기다리는 동안
   * 입력을 고쳐도 지난 요청의 에러가 새 값 아래에 뜨지 않는다(성공 경로와 같은 방식).
   */
  const [error, setError] = useState<{ for: string; message: string } | null>(
    null,
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const errorId = useId();
  const { mutate, isPending } = useConvertDart(HOME_CONVERT_MUTATION_KEY, {
    silent: true,
  });

  // 형식 판정은 type="url" 의 native 검증을 그대로 쓴다(정규식 불필요).
  const isFormatInvalid = () =>
    !!url.trim() && !!inputRef.current?.validity.typeMismatch;
  const errorMessage = error?.for === url.trim() ? error.message : null;

  return (
    <>
      <Notice
        details={[
          "사업보고서에 첨부된 감사보고서",
          "반기/분기보고서에 첨부된 검토보고서",
        ]}
      >
        보고서에 첨부된 <span className="font-semibold">감사 · 검토보고서</span>
        의 DART URL만 변환 가능합니다.
      </Notice>
      <div className="flex w-full flex-col gap-1">
        <label
          className={cn(
            "flex w-full items-center gap-2 rounded-lg border bg-white px-3 py-2",
            errorMessage
              ? "border-status-negative"
              : "border-line-normal focus-within:border-blue-500",
          )}
        >
          <Link className="size-3.5 shrink-0 text-label-alternative" />
          <input
            ref={inputRef}
            type="url"
            value={url}
            // 값이 바뀌면 지난 판정은 무효 — 입력 완료(blur·변환) 시점에 다시 본다
            onChange={(e) => setUrl(e.target.value)}
            // blur 는 형식 에러만 켠다. 서버가 돌려준 에러를 입력 없이 지우면 안 된다
            onBlur={() => {
              if (isFormatInvalid())
                setError({ for: url.trim(), message: DART_URL_FORMAT_ERROR });
            }}
            placeholder="DART 공시 URL 입력"
            aria-invalid={!!errorMessage}
            aria-describedby={errorMessage ? errorId : undefined}
            className="body-1-normal-regular min-w-0 flex-1 bg-transparent text-label-normal outline-none placeholder:text-neutral-400"
          />
        </label>
        {errorMessage && (
          <p id={errorId} className="caption-1-regular text-status-negative">
            {errorMessage}
          </p>
        )}
      </div>
      <div className="mt-2 flex w-full items-center gap-4">
        <SelectInput
          className="flex-1"
          label="변환 형식"
          value={format}
          options={DART_FORMAT_ITEMS}
          onChange={setFormat}
        />
        <ConvertButton
          pending={isPending}
          disabled={!url.trim()}
          onClick={() => {
            const sent = url.trim();
            if (isFormatInvalid()) {
              setError({ for: sent, message: DART_URL_FORMAT_ERROR });
              return;
            }
            setError(null);
            logHomeWork(STEP_LABEL, "공시 불러오기");
            mutate(
              { url: sent, toFormat: format },
              {
                onSuccess: () =>
                  setUrl((cur) => (cur.trim() === sent ? "" : cur)),
                onError: (e) =>
                  setError({ for: sent, message: dartConvertErrorMessage(e) }),
              },
            );
          }}
        />
      </div>
    </>
  );
};

const AccountingSheetTab = () => {
  const [file, setFile] = useState<File | null>(null);
  const [reportType, setReportType] =
    useState<AccountingSheetReportType>("audit");
  const [year, setYear] = useState(String(CURRENT_YEAR_KST));
  const [period, setPeriod] = useState<AccountingSheetPeriod>("quarterly_1");
  const [scope, setScope] = useState<ConsolidationType>("consolidated");
  const { mutate, isPending } = useConvertAccountingSheet(
    HOME_CONVERT_MUTATION_KEY,
  );

  return (
    <>
      <Notice>
        정산표(excel) 파일을 업로드하고 보고서 종류를 선택해주세요
      </Notice>
      {/* ponytail: .xlsm 도 받지만 안내 문구는 디자인대로 xlsx 만 적는다 */}
      <FileDropZone
        accept=".xlsx,.xlsm"
        hint="*.xlsx 형식"
        file={file}
        onFileChange={setFile}
      />
      <div className="flex w-full flex-col gap-2">
        <p className="label-1-normal-semibold text-label-neutral">
          보고서 종류
        </p>
        <div className="flex gap-2">
          <SelectInput
            className="flex-1"
            label="보고서 구분"
            value={reportType}
            options={REPORT_FORMAT_ITEMS}
            onChange={setReportType}
          />
          <SelectInput
            className="flex-1"
            label="회계연도"
            value={year}
            options={YEAR_ITEMS}
            onChange={setYear}
          />
          {/* 감사전은 기간 개념이 없다(API 가 period 를 받지 않음) */}
          <SelectInput
            className="flex-1"
            label="보고 기간"
            value={period}
            options={PERIOD_ITEMS}
            onChange={setPeriod}
            disabled={reportType === "pre_audit"}
          />
          <SelectInput
            className="flex-1"
            label="연결 구분"
            value={scope}
            options={CONSOLIDATION_ITEMS}
            onChange={setScope}
          />
        </div>
      </div>
      <ConvertButton
        pending={isPending}
        disabled={!file}
        className="mt-2"
        onClick={() => {
          if (!file) return;
          logHomeWork(STEP_LABEL, "정산표 → DSD 변환");
          mutate(
            {
              file,
              params: {
                reportType,
                scope,
                period: reportType === "audit" ? period : undefined,
                year: Number(year),
              },
            },
            { onSuccess: () => setFile((cur) => (cur === file ? null : cur)) },
          );
        }}
      />
    </>
  );
};

/** STEP1 기초 보고서 작업 — 변환 기능 세 개를 탭으로 둔다. */
export const BaseReportPanel = () => {
  const [tab, setTab] = useState<Tab>("dsd-excel");

  return (
    <div className="flex w-full flex-col items-end gap-4">
      <ViewSwitchGroup
        size="36px"
        options={TABS}
        value={tab}
        onChange={setTab}
        className="flex w-full [&>button]:flex-1"
      />
      {/* 탭을 오가도 업로드한 파일·입력값이 남도록 셋 다 마운트해 두고 숨긴다 */}
      {TABS.map(({ value }) => (
        <div
          key={value}
          hidden={tab !== value}
          className={cn(
            "flex w-full flex-col items-end gap-4",
            tab !== value && "hidden",
          )}
        >
          {value === "dsd-excel" && <DsdExcelTab />}
          {value === "dart" && <DartTab />}
          {value === "accounting-sheet" && <AccountingSheetTab />}
        </div>
      ))}
    </div>
  );
};
